import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

function countCartProducts(items) {
  const groups = new Set(items.filter(item => item.handle !== 'roxxlyn-upgrade').map(item => item.properties?._rxk_group).filter(Boolean));
  return items.reduce((count, item) => count + (item.handle === 'roxxlyn-upgrade' && groups.has(item.properties?._rxk_group) ? 0 : item.quantity), 0);
}

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  #countRevision = 0;
  #drawerObserver;
  #drawerMarker;
  #drawerCount;
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    const drawer = document.getElementById('cart-drawer');
    if (drawer) {
      this.#drawerMarker = drawer.querySelector('[data-rxk-product-count]');
      this.#drawerCount = this.#drawerMarker?.dataset.rxkProductCount;
      this.#drawerObserver = new MutationObserver(this.syncDrawerProductCount);
      this.#drawerObserver.observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-rxk-product-count'] });
    }
  }

  disconnectedCallback() {
    this.#countRevision++;
    this.#drawerObserver?.disconnect();
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  // The configurator replaces drawer HTML without emitting Horizon's cart event.
  syncDrawerProductCount = () => {
    const marker = document.querySelector('#cart-drawer [data-rxk-product-count]');
    if (!marker || (marker === this.#drawerMarker && marker.dataset.rxkProductCount === this.#drawerCount)) return;
    this.#drawerMarker = marker;
    this.#drawerCount = marker.dataset.rxkProductCount;
    const count = Number(this.#drawerCount);
    if (Number.isInteger(count) && count >= 0 && this.isConnected) {
      this.#countRevision++;
      this.renderCartBubble(count);
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    const revision = ++this.#countRevision;
    event.promise
      ?.then(async ({ detail }) => {
        let itemCount;
        for (const html of Object.values(detail?.sections || {})) {
          if (typeof html !== 'string') continue;
          const marker = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-rxk-product-count]');
          if (!marker) continue;
          const value = Number(marker.dataset.rxkProductCount);
          if (Number.isInteger(value) && value >= 0) { itemCount = value; break; }
        }
        if (itemCount === undefined) itemCount = await this.fetchProductCount();
        if (revision === this.#countRevision && this.isConnected) this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    document.dispatchEvent(new CustomEvent('rxk:cart-count', { detail: { count: itemCount } }));

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  fetchProductCount = async () => {
    const response = await fetch(`${Theme.routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Cart count: HTTP ${response.status}`);
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error('Cart count: missing items');
    return countCartProducts(cart.items);
  };

  ensureCartBubbleIsCorrect = async () => {
    const revision = ++this.#countRevision;
    try {
      const count = await this.fetchProductCount();
      if (revision === this.#countRevision && this.isConnected) this.renderCartBubble(count, false);
    } catch (error) {
      console.warn('[cart-icon] Could not refresh product count:', error);
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
